import { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { Modal } from 'src/components/baseComponents';
import { MentalHealthRaw } from 'src/utilities/PromisMH';
import { PhysicalHealthRaw } from 'src/utilities/PromisPH';
import { toast } from '@insight-health-ai/roseate/components/toast';
import { CommonFormProps } from '.';
import { analytics } from 'src/services/analytics';
import { getAppointmentDetailsForAnalytics } from 'src/utils';

type Props = CommonFormProps;

export const PROMIS = ({
  isOpen,
  appointment,
  form,
  onSave,
  loading,
}: Props) => {
  const promisform = form.content as any[];

  const [initialValues, setInitialValues] = useState({});
  const [fields, setFields] = useState<any[]>([]);
  const [validationSchema, setValidationSchema] =
    useState<Yup.ObjectSchema<any>>();

  useEffect(() => {
    const updatedValidationSchema = promisform?.reduce((schema, item) => {
      if (item.optional === 'false') {
        const key = item.item.toLowerCase().replace(/\s+/g, '_');
        const validationRule =
          item.type === 'checkbox'
            ? Yup.bool().oneOf([true], `${item.item} must be checked`)
            : Yup.string().required(`${item.item} is required`);

        return schema.shape({
          [key]: validationRule,
        });
      }

      return schema;
    }, Yup.object());
    setValidationSchema(updatedValidationSchema);
  }, []);

  useEffect(() => {
    const updatedValuesArray = promisform?.map((curr) => {
      const key = curr.item.toLowerCase();
      return { [key]: '' };
    });

    const updatedValues = Object.assign({}, ...updatedValuesArray);
    setInitialValues((prevValues) => ({ ...prevValues, ...updatedValues }));
  }, []);

  useEffect(() => {
    const transformedArray = promisform.map((item) => {
      if (item.type !== 'header' && item.type !== 'label') {
        const transformedItem = {
          name: item.item.toLowerCase(),
          type: item.type,
          title: item.item,
          placeholder: item.placeholder ? item.placeholder : '',
          autoComplete: 'off',
        };
        Object.assign(transformedItem, { options: item.options });
        return transformedItem;
      }
      return {
        name: item.item,
        type: item.type,
        title: item.item,
      };
    });
    setFields(transformedArray);
  }, []);

  const calculatePROMIS = (answer: string) => {
    let PROMISValue = 0;

    switch (answer) {
      case '0':
        PROMISValue = 5;
        break;
      case '1':
      case '2':
      case '3':
        PROMISValue = 4;
        break;
      case '4':
      case '5':
      case '6':
        PROMISValue = 3;
        break;
      case '7':
      case '8':
      case '9':
        PROMISValue = 2;
        break;
      case '10':
        PROMISValue = 1;
        break;
      default:
        break;
    }

    return PROMISValue;
  };
  const scoreMap1_5_9r: Record<string, number> = {
    Excellent: 5,
    'Very Good': 4,
    Good: 3,
    Fair: 2,
    Poor: 1,
  };
  const scoreMap_6: Record<string, number> = {
    Completely: 5,
    Mostly: 4,
    Moderately: 3,
    'A little': 2,
    'Not at all': 1,
  };

  const scoreMap10r: Record<string, number> = {
    Never: 5,
    Rarely: 4,
    Sometimes: 3,
    Often: 2,
    Always: 1,
  };

  const scoreMap_8r: Record<string, number> = {
    None: 5,
    Mild: 4,
    Moderate: 3,
    Severe: 2,
    'Very Severe': 1,
  };

  const handleSubmit = (values: Record<string, any>) => {
    analytics.track(
      'Clicked Submit In PROMIS Form',
      getAppointmentDetailsForAnalytics(appointment!)
    );
    if (validationSchema) {
      return validationSchema
        .validate(values)
        .then(() => {
          const filteredValues = Object.keys(values).reduce(
            (acc, key) => {
              if (values[key] !== '') {
                acc[key] = values[key];
              }
              return acc;
            },
            {} as Record<string, any>
          );
          const painRating7r =
            values['7rc'][
              ' how would you rate your pain on average? (0- no pain and 10 - worst pain imaginable)'
            ];
          const PROMISValue = calculatePROMIS(painRating7r);

          const question3Value =
            values['3'][
              ' in general, how would you rate your physical health?'
            ]; // Assuming the key is "

          const question6Value =
            values['6'] &&
            values['6'][
              ' to what extent are you able to carry out your everyday physical activities such as walking, climbing stairs, carrying groceries, or moving a chair?'
            ];

          const question8rValue =
            values['8r'][' how would you rate your fatigue on average?'];

          const question2Value =
            values['2'][' in general, would you say your quality of life is'];

          const question4Value =
            values['4'][
              ' in general, how would you rate your mental health, including your mood and your ability to think?'
            ];
          const question5Value =
            values['5'][
              ' in general, how would you rate your satisfaction with your social activities and relationships?'
            ];

          const question10rValue =
            values['10r'][
              ' how often have you been bothered by emotional problems such as feeling anxious, depressed or irritable?'
            ];
          const score2 = scoreMap1_5_9r[question2Value];
          const score4 = scoreMap1_5_9r[question4Value];
          const score5 = scoreMap1_5_9r[question5Value];
          const score10r = scoreMap10r[question10rValue];
          const score3 = scoreMap1_5_9r[question3Value];
          const score6 = scoreMap_6[question6Value];
          const score7r = PROMISValue;
          const score8r = scoreMap_8r[question8rValue];
          const questionsSum3_6_7r_8r = score3 + score6 + score7r + score8r;
          const questionsSum2_4_5_10r = score2 + score4 + score5 + score10r;
          const PhysicalHealthScore = PhysicalHealthRaw.find(
            (item) => item.Physical_raw_score === questionsSum3_6_7r_8r
          );

          const MentalHealth = MentalHealthRaw.find(
            (item) => item.Mental_raw_score === questionsSum2_4_5_10r
          );
          const payload = {
            response: {
              PhysicalHealthScore: PhysicalHealthScore,
              MentalHealth: MentalHealth,
              options: filteredValues,
            },
            form_type: 'PROMIS',
          };
          onSave(payload);
        })
        .catch(() => {
          toast.error('Please fill all the fields before proceeding');
        });
    } else {
      toast.error('Please fill all the fields before proceeding');
    }
  };

  return (
    <div>
      {/** @ts-ignore */}
      <Modal
        open={isOpen}
        className={'review-of-system-modal'}
        formClassName={'grid grid-cols-2 gap-4'}
        initialValues={initialValues}
        validationSchema={validationSchema}
        submitButtonText="Submit"
        cancelButtonText=""
        closable={false}
        centered={true}
        fields={fields}
        onSubmitLoading={loading}
        disableSubmit={loading}
        handleSubmit={handleSubmit}
        promisform={true}
      />
    </div>
  );
};
